import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { Sidebar } from './Sidebar';
import { AudioPlayer } from './AudioPlayer';
import { NotificationBell } from './NotificationBell';
import { MessageButton } from './MessageButton';
import { UserMenu } from './UserMenu';
import { PWAInstallPrompt } from '../pwa/PWAInstallPrompt';
import { EmailVerificationBanner } from './EmailVerificationBanner';
import { PublishingNudgeBanner } from '../publishing/PublishingNudgeBanner';
import { KeyboardShortcutsOverlay, useKeyboardShortcutsOverlay } from '../KeyboardShortcutsOverlay';
import PromotionCreditReminderModal from '../PromotionCreditReminderModal';
import { StillListeningPrompt } from '../ptl/StillListeningPrompt';
import { PtlEarningsPop } from '../ptl/PtlEarningsPop';
import { AdFreeUpgradeChip } from '../radio/AdFreeUpgradeChip';
import { Menu, X, LogIn, UserPlus, ShoppingCart } from 'lucide-react';

export const Layout = ({ children, hideNavbar = false }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPWAPrompt, setShowPWAPrompt] = useState(false);
  const { user, token, isFirstLogin, clearFirstLogin } = useAuth();
  const { cart, setIsOpen: setCartOpen } = useCart();
  const navigate = useNavigate();
  const isAuthenticated = !!token;

  // Global keyboard shortcuts overlay (Cmd/Ctrl + ? toggles).
  const shortcuts = useKeyboardShortcutsOverlay();

  // Show PWA install prompt for first-time users
  useEffect(() => {
    if (isFirstLogin && isAuthenticated) {
      // Delay slightly to let the page settle
      const timer = setTimeout(() => {
        setShowPWAPrompt(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isFirstLogin, isAuthenticated]);

  const handlePWAPromptDismiss = () => {
    setShowPWAPrompt(false);
    clearFirstLogin();
  };

  // Close sidebar when route changes on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar when clicking outside on mobile
  const handleOverlayClick = () => {
    setSidebarOpen(false);
  };

  // Full-width layout without sidebar for profile pages
  if (hideNavbar) {
    return (
      <div className="min-h-screen bg-[#000000]">
        {/* Minimal Top Bar for hideNavbar mode */}
        <div className="fixed top-0 left-0 right-0 h-14 bg-black/50 backdrop-blur-sm flex items-center justify-between px-4 md:px-8 z-50">
          <button
            onClick={() => window.history.back()}
            className="text-white/60 hover:text-white font-mono text-sm"
          >
            ← Back
          </button>
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <>
                {/* Cart Button */}
                {cart.item_count > 0 && (
                  <button
                    onClick={() => setCartOpen(true)}
                    className="relative p-2 text-[#888888] hover:text-white transition-colors"
                    data-testid="cart-nav-btn-hidden"
                  >
                    <ShoppingCart size={20} />
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FFDA33] text-black text-xs font-mono rounded-full flex items-center justify-center">
                      {cart.item_count}
                    </span>
                  </button>
                )}
                <MessageButton />
                <NotificationBell />
                <UserMenu />
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/login')}
                  className="flex items-center gap-2 px-4 py-2 text-[#888888] hover:text-white font-mono text-sm transition-colors"
                >
                  <LogIn size={16} /> Login
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="flex items-center gap-2 px-4 py-2 bg-[#00CCFF] text-black font-mono text-sm hover:bg-[#00c8ff] transition-colors"
                >
                  <UserPlus size={16} /> Sign Up
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Content */}
        <main className="min-h-screen">
          {children}
        </main>
        
        {/* Audio Player - only for authenticated users */}
        {isAuthenticated && <AudioPlayer />}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000000]">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/80 z-40 md:hidden"
          onClick={handleOverlayClick}
        />
      )}

      {/* Sidebar - Hidden on mobile unless open. iter_407 — preserve-dark
       * was removed so the sidebar adapts to light theme (was forcing
       * the whole sidebar to stay dark in light mode, which the user
       * explicitly objected to). The Sidebar component's own
       * `bg-[#000000]` is now overridden by the light-theme repaint
       * rules in index.css. */}
      <div className={`
        fixed left-0 top-0 bottom-0 z-50
        transform transition-transform duration-300 ease-in-out
        md:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <Sidebar onNavigate={() => setSidebarOpen(false)} />
      </div>

      {/* Top Bar — iter_407: preserve-dark removed so the top bar
       * adapts to light theme alongside the rest of the chrome. */}
      <div className="fixed top-0 left-0 md:left-48 right-0 h-14 bg-[#000000] border-b border-[#1a1a1a] flex items-center justify-between px-4 md:px-6 z-30">
        {/* Mobile Menu Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="md:hidden p-2 text-[#888888] hover:text-white"
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Mobile Logo — new circular brand mark with full wordmark. */}
        <img 
          src="/onevoice-logo-circular.png" 
          alt="One Voice Distribution™"
          className="h-12 w-auto object-contain md:hidden"
          loading="lazy"
          decoding="async"
          data-testid="mobile-header-logo"
        />

        {/* Spacer for desktop */}
        <div className="hidden md:block" />

        {/* Right side - Messages, Notifications and User Menu (or Login/Signup) */}
        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <>
              {/* Cart Button */}
              {cart.item_count > 0 && (
                <button
                  onClick={() => setCartOpen(true)}
                  className="relative p-2 text-[#888888] hover:text-[#FFDA33] transition-colors"
                  data-testid="cart-nav-btn"
                >
                  <ShoppingCart size={20} />
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FFDA33] text-black text-xs font-mono rounded-full flex items-center justify-center">
                    {cart.item_count}
                  </span>
                </button>
              )}
              <MessageButton />
              <NotificationBell />
              <UserMenu />
            </>
          ) : (
            <>
              <button
                onClick={() => navigate('/login')}
                className="flex items-center gap-1 px-3 py-1.5 text-[#888888] hover:text-white font-mono text-xs transition-colors"
              >
                <LogIn size={14} /> Login
              </button>
              <button
                onClick={() => navigate('/register')}
                className="flex items-center gap-1 px-3 py-1.5 bg-[#00CCFF] text-black font-mono text-xs hover:bg-[#00c8ff] transition-colors"
              >
                <UserPlus size={14} /> Sign Up
              </button>
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <EmailVerificationBanner />
      <PublishingNudgeBanner />
      <KeyboardShortcutsOverlay open={shortcuts.open} onOpenChange={shortcuts.setOpen} />
      <main className="md:ml-48 pt-14 pb-24 md:pb-28 min-h-screen">
        {children}
      </main>

      {/* Daily nudge for unused weekly-winner promotion credit */}
      {isAuthenticated && <PromotionCreditReminderModal />}

      {/* Footer with Legal Links — CloudFlare-style bar-separated rows.
          Inline `|` separators wrap WITH the adjacent links so when a row
          breaks the separator goes with it. */}
      <footer className="md:ml-48 py-6 px-4 border-t border-[#1a1a1a] bg-black" data-testid="layout-footer">
        <div className="max-w-[1600px] mx-auto flex flex-col gap-3">
          {/* Row 1 — link cluster (bar-separated, wraps naturally) */}
          <nav
            className="flex flex-wrap justify-center md:justify-start items-center gap-y-1 text-xs font-mono"
            data-testid="footer-link-row"
          >
            {[
              {
                label: 'Paid To Listen',
                onClick: () => navigate('/paid-to-listen/info'),
                cls: 'text-[#00ff88] hover:text-white',
                tid: 'footer-paid-to-listen-info',
              },
              {
                label: 'Privacy Policy',
                onClick: () => navigate('/privacy'),
                cls: 'text-[#888] hover:text-[#00CCFF]',
              },
              {
                label: 'Terms of Service',
                onClick: () => navigate('/terms'),
                cls: 'text-[#888] hover:text-[#00CCFF]',
              },
              {
                label: 'Section 230',
                onClick: () => navigate('/terms#section-230'),
                cls: 'text-[#888] hover:text-[#FFDA33]',
                tid: 'footer-section-230-link',
                title: 'Section 230 of the Communications Decency Act — 47 U.S.C. § 230',
              },
              {
                label: 'Contact',
                href: 'mailto:support@onevoicedistro.com',
                cls: 'text-[#888] hover:text-[#00CCFF]',
              },
            ].map((item, i, arr) => {
              const linkClass = `px-2 py-1 leading-tight ${item.cls} transition-colors`;
              const linkEl = item.href ? (
                <a href={item.href} className={linkClass} data-testid={item.tid}>
                  {item.label}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={item.onClick}
                  className={linkClass}
                  data-testid={item.tid}
                  title={item.title}
                >
                  {item.label}
                </button>
              );
              return (
                <span key={item.label} className="inline-flex items-center">
                  {linkEl}
                  {i < arr.length - 1 && (
                    <span aria-hidden className="text-[#333] select-none">|</span>
                  )}
                </span>
              );
            })}
          </nav>

          {/* Row 2 — copyright, centered on mobile / left-aligned on desktop */}
          <p
            className="text-[#FFDA33] font-['Chakra_Petch'] uppercase tracking-[0.25em] text-[10px] text-center md:text-left mb-1"
            data-testid="footer-slogan"
          >
            One voice. Your choice. Press play. Get paid.
          </p>
          <p className="text-[#888] font-mono text-xs text-center md:text-left" data-testid="footer-copyright">
            © {new Date().getFullYear()} One Voice Distribution™. All rights reserved. ·
            OneVoice™, OneVoice Radio™, OneVoice Studio™, AI Studio™, Song Wars™ &amp; Music Review™
            are trademarks of One Voice Distribution.
          </p>
          <p
            className="text-[#444] font-mono text-[10px] leading-relaxed text-center md:text-left"
            data-testid="footer-section-230-notice"
          >
            One Voice Distribution is an interactive computer service under
            47 U.S.C. § 230(c). Content posted by users is the responsibility
            of its authors, not the platform.{' '}
            <button
              onClick={() => navigate('/terms#section-230')}
              className="underline hover:text-[#FFDA33] transition-colors"
            >
              Learn more
            </button>.
          </p>
        </div>
      </footer>

      {/* Audio Player - only for authenticated users */}
      {isAuthenticated && <AudioPlayer />}

      {/* PTL "Still listening?" gate — only renders when the backend
          flags a confirmation as needed. Visible across the app so the
          listener never misses it. */}
      {isAuthenticated && <StillListeningPrompt token={token} />}

      {/* PTL micro-celebration — confetti chip pops in the bottom-right
          for ~1.2s after every successful record_play. Tiny dopamine
          hit per play that makes earnings feel real in the moment. */}
      {isAuthenticated && <PtlEarningsPop />}

      {/* "Skip ads forever — Go VIP / Join PTL" chip. Globally mounted
          so the moment any ad fires for a non-VIP / non-PTL listener,
          the dual-CTA chip surfaces for ~6s. Visible to anonymous
          visitors too — converts free listeners into VIPs / PTLers. */}
      <AdFreeUpgradeChip />

      {/* PWA Install Prompt - show for first-time users */}
      {showPWAPrompt && (
        <PWAInstallPrompt 
          isFirstLogin={true} 
          onDismiss={handlePWAPromptDismiss} 
        />
      )}
    </div>
  );
};
